/**
 * PlainText Component implemented with Material UI
 * 
 * This component uses MUI's Typography component to render text with various styles
 * and provides subcomponents for Error, Info, and Hyperlink text.
 */

import React from 'react';
import { Typography, Link, TypographyProps, LinkProps, useTheme as useMuiTheme } from '@mui/material';
import { PlainTextProps, ErrorProps, InfoProps, HyperlinkProps } from 'types/components/atoms';
import { useTheme } from 'theme/ThemeContext';

/**
 * PlainText component
 * 
 * @param props - Component props
 * @returns The Typography component with custom styling
 */
const PlainText: React.FC<PlainTextProps> = (props) => {
  const {
    content,
    type,
    bold,
    color,
    size,
    className,
    style,
    // Filter out non-DOM props that should not be passed to MUI components
    show,
    dispatcher,
    ...otherProps
  } = props as PlainTextProps & { show?: boolean; dispatcher?: any };

  // Map the type prop to MUI Typography variant
  const getVariant = (): TypographyProps['variant'] => {
    switch (type) {
      case 'h1': return 'h1';
      case 'h2': return 'h2';
      case 'h3': return 'h3';
      case 'h4': return 'h4';
      case 'h5': return 'h5';
      case 'h6': return 'h6';
      case 'p':
      default: return 'body1';
    }
  };

  // For anchor tags, use MUI Link component
  if (type === 'a') {
    return (
      <Link
        className={className}
        sx={{
          fontWeight: bold ? 'bold' : 'normal',
          whiteSpace: content?.includes('\n') ? 'pre-wrap' : 'normal',
          color: color,
          fontSize: `${size}px`,
          ...style
        }}
        {...otherProps as LinkProps}
      >
        {content}
      </Link>
    );
  }

  // For all other elements, use Typography
  return (
    <Typography
      variant={getVariant()}
      component={type || 'div'}
      className={className}
      sx={{
        fontWeight: bold ? 'bold' : 'normal',
        whiteSpace: content?.includes('\n') ? 'pre-wrap' : 'normal',
        marginBottom: 0,
        color: color,
        fontSize: size ? `${size}px` : undefined,
        ...style
      }}
      {...otherProps as TypographyProps}
    >
      {content}
    </Typography>
  );
};

/**
 * Error component
 * 
 * @param props - Component props
 * @returns The PlainText component with error styling
 */
export const Error: React.FC<ErrorProps> = ({ msg, isNav = false }) => {
  const props: PlainTextProps = {
    className: `${isNav ? 'text-start p-1' : 'text-center p-2'}`,
    type: 'p',
    size: 16,
    color: '#dc3545',
    content: msg
  };
  return <PlainText {...props} />;
};

/**
 * MuiError component for backward compatibility
 */
export const MuiError: React.FC<ErrorProps> = ({ msg, isNav = false }) => {
  const props: PlainTextProps = {
    className: `${isNav ? 'text-start p-1' : 'text-center p-2'}`,
    type: 'p',
    size: 16,
    color: '#dc3545',
    content: msg
  };
  return <PlainText {...props} />;
};

/**
 * Info component
 * 
 * @param props - Component props
 * @returns The PlainText component with info styling
 */
export const Info: React.FC<InfoProps> = ({ msg }) => {
  const props: PlainTextProps = {
    className: 'text-center p-2',
    type: 'p',
    size: 16,
    color: 'blue',
    content: msg
  };
  return <PlainText {...props} />;
};

/**
 * MuiInfo component for backward compatibility
 */
export const MuiInfo: React.FC<InfoProps> = ({ msg }) => {
  const props: PlainTextProps = {
    className: 'text-center p-2',
    type: 'p',
    size: 16,
    color: 'blue',
    content: msg
  };
  return <PlainText {...props} />;
};

/**
 * Hyperlink component
 * 
 * @param props - Component props
 * @returns The PlainText component with hyperlink styling
 */
export const Hyperlink: React.FC<HyperlinkProps> = ({ link, msg, isNav = false }) => {
  const props: PlainTextProps = {
    type: 'a',
    size: 16,
    color: 'var(--app-red)',
    target: 'blank',
    href: link,
    content: msg
  };
  return (
    <div className={`${isNav ? 'text-start' : 'text-center'} p-1`}>
      <PlainText {...props} />
    </div>
  );
};

/**
 * MuiHyperlink component for backward compatibility
 */
export const MuiHyperlink: React.FC<HyperlinkProps> = ({ link, msg, isNav = false }) => {
  const muiTheme = useMuiTheme();
  const { mode } = useTheme();
  
  const props: PlainTextProps = {
    type: 'a',
    size: 16,
    color: muiTheme.palette.text.primary,
    target: 'blank',
    href: link,
    content: msg
  };
  return (
    <div className={`${isNav ? 'text-start' : 'text-center'} p-1`}>
      <Link
        href={link}
        target="_blank"
        sx={{
          color: muiTheme.palette.text.primary,
          fontFamily: muiTheme.typography.fontFamily,
          fontSize: '14px',
          fontWeight: 400,
          textDecoration: 'underline',
          '&:hover': {
            textDecoration: 'underline',
            color: muiTheme.palette.text.secondary,
          },
        }}
      >
        {msg}
      </Link>
    </div>
  );
};

export type { PlainTextProps, ErrorProps, InfoProps, HyperlinkProps };
export default PlainText;
